import { useState } from 'react';
import { toast } from 'react-toastify';
import TextEdit from '../../../molecules/TextEdit';
import appConfig from '../../../../config/app-config';
import Dialog from '../../../organisms/Dialog';

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const BASE_URL = COMMUNICATION_ENV.address;
const API_KEY = COMMUNICATION_ENV.key;

interface CallRegistryModalFormProps {
  contactId: string;
  onClose: () => void;
  onSuccess: (newActivity: any) => void;
}

export default function CallRegistryModalForm({ contactId, onClose, onSuccess }: CallRegistryModalFormProps) {
  const isoDate = new Date().toISOString();
  const defaultDateTime = isoDate.substring(0, isoDate.lastIndexOf(":"));
  
  const [reason, setReason] = useState('');
  const [observation, setObservation] = useState('');
  const [callDateTime, setCallDateTime] = useState(defaultDateTime);
  const [reasonPrefix, setReasonPrefix] = useState<'VALIDACAO' | 'COBRANCA' | 'PERSONALIZADO'>('VALIDACAO');
  const [isSending, setIsSending] = useState(false);

  const buildReason = () => {
    const prefix = reasonPrefix === 'VALIDACAO' ? 'Validação' : reasonPrefix === 'COBRANCA' ? 'Cobrança' : '';
    return prefix || reason;
  };

  const handleSubmit = async (answered: boolean) => {
    if (isSending) return;
    setIsSending(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${BASE_URL}/contacts/${contactId}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY || '',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          title: buildReason(),
          occurredAt: callDateTime,
          interactionType: 'CALL',
          body: observation,
          answered: answered ? 'Atendido' : 'Não atendido',
        }),
      });

      if (response.ok) {
        const newActivity = await response.json();
        toast.success('Ligação registrada com sucesso!');
        onSuccess(newActivity);
        onClose();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.message || 'Erro ao registrar ligação');
      }
    } catch (error) {
      toast.error('Erro ao registrar ligação');
    } finally {
      setIsSending(false);
    }
  };

  const Spinner = () => (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  return (
    <Dialog
      id="call-registry-modal"
      show={true}
      onClose={onClose}
      header="Registrar Ligação"
      size="md"
      body={
        <div className="p-6 space-y-4">
          <div>
            <input type="datetime-local" value={callDateTime} onChange={(e) => setCallDateTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Motivo</label>
            <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5 w-fit">
              <button type="button" onClick={() => setReasonPrefix('VALIDACAO')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${reasonPrefix === 'VALIDACAO' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Validação</button>
              <button type="button" onClick={() => setReasonPrefix('COBRANCA')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${reasonPrefix === 'COBRANCA' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Cobrança</button>
              <button type="button" onClick={() => setReasonPrefix('PERSONALIZADO')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${reasonPrefix === 'PERSONALIZADO' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Personalizado</button>
            </div>
          </div>
          {reasonPrefix === 'PERSONALIZADO' && (
            <TextEdit label="Motivo personalizado" value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Observação</label>
            <textarea value={observation} onChange={(e) => setObservation(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white resize-none" rows={4} placeholder="Digite uma observação sobre a ligação..." />
          </div>
        </div>
      }
      footer={
        <div className="flex gap-3 w-full">
          <button onClick={() => handleSubmit(false)} disabled={isSending} className="flex-1 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {isSending && <Spinner />} Não atendido
          </button>
          <button onClick={() => handleSubmit(true)} disabled={isSending} className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {isSending && <Spinner />} Atendido
          </button>
        </div>
      }
    />
  );
}
